// Ilustrações dos estados vazios. Um ícone de 16px dentro de um quadradinho
// cinza é o mesmo desenho em toda tela vazia do app — não diz nada sobre o que
// está faltando ali. Cada ilustração daqui desenha o OBJETO que a tela ainda
// não tem (o papel, a automação, a fatura, a chave, o chamado), para o vazio
// parecer um começo e não um erro.
//
// Regras de composição, para as seis parecerem a mesma mão:
//  - viewBox 160×120 e nada fora dele;
//  - só `currentColor` (herdado de `text-muted-foreground`), com três pesos:
//    superfície (opacity ~.06), contorno (~.25) e conteúdo (~.2 a .35). Assim
//    funcionam em claro e escuro sem uma cor sequer codificada;
//  - um único acento em `text-primary` por ilustração — o elemento que
//    representa a ação que a pessoa vai fazer ali;
//  - `aria-hidden`: o texto do empty state é quem informa; a ilustração é
//    decoração.
import type { ComponentProps } from "react";
import { cn } from "@/lib/utils";

function Illustration({
  className,
  children,
  groundY,
  groundRx,
  ...props
}: ComponentProps<"svg"> & { groundY: number; groundRx: number }) {
  return (
    <svg
      viewBox="0 0 160 120"
      fill="none"
      aria-hidden="true"
      className={cn("h-24 w-40 text-muted-foreground", className)}
      {...props}
    >
      {children}
      {/* Sombra de contato: sem ela o desenho fica boiando no card. Cada
          ilustração passa a própria linha do chão — uma elipse fixa ficava
          longe demais das composições mais baixas. */}
      <ellipse cx="80" cy={groundY} rx={groundRx} ry="5" className="fill-current opacity-[0.08]" />
    </svg>
  );
}

// Chaves da conexão com o CRM: o Disk de um lado (a onda das ligações) e o
// CRM do outro (a lista de contatos), ligados por um cabo. A chave no meio do
// cabo é o acento, junto com o próprio cabo: é ela que autentica o que passa de
// um lado para o outro, e é o que a pessoa vai gerar aqui. (Não é a
// `ApiKeyIllustration`: lá a chave está sozinha no cartão; aqui o objeto é a
// ligação entre os dois sistemas.)
export function CrmConnectionIllustration(props: ComponentProps<"svg">) {
  return (
    <Illustration groundY={96} groundRx={62} {...props}>
      <rect x="12" y="30" width="46" height="58" rx="10" className="fill-current opacity-[0.06]" />
      <rect
        x="12"
        y="30"
        width="46"
        height="58"
        rx="10"
        className="stroke-current opacity-25"
        strokeWidth="2"
      />
      <rect x="22" y="40" width="20" height="5" rx="2.5" className="fill-current opacity-30" />
      {[
        [24, 8],
        [30, 18],
        [36, 12],
        [42, 22],
        [48, 10],
      ].map(([x, h]) => (
        <rect
          key={x}
          x={x - 1.5}
          y={68 - h / 2}
          width="3"
          height={h}
          rx="1.5"
          className="fill-current opacity-25"
        />
      ))}
      <rect x="102" y="30" width="46" height="58" rx="10" className="fill-current opacity-[0.06]" />
      <rect
        x="102"
        y="30"
        width="46"
        height="58"
        rx="10"
        className="stroke-current opacity-25"
        strokeWidth="2"
      />
      {[42, 56, 70].map((y, i) => (
        <g key={y}>
          <circle cx="114" cy={y + 3} r="4" className="fill-current opacity-20" />
          <rect
            x="122"
            y={y + 0.5}
            width={i === 1 ? 14 : 18}
            height="5"
            rx="2.5"
            className="fill-current opacity-25"
          />
        </g>
      ))}
      <path
        d="M58 59h9m26 0h9"
        className="stroke-current text-primary"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <circle cx="58" cy="59" r="3" className="fill-current text-primary" />
      <circle cx="102" cy="59" r="3" className="fill-current text-primary" />
      <circle cx="80" cy="59" r="13" className="fill-current text-primary opacity-[0.12]" />
      <circle cx="80" cy="59" r="13" className="stroke-current text-primary" strokeWidth="2" />
      <circle cx="75" cy="59" r="3.5" className="stroke-current text-primary" strokeWidth="2" />
      <path
        d="M78.5 59h9m-3 0v3.5m-3-3.5v2.5"
        className="stroke-current text-primary"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Illustration>
  );
}

// Papéis e permissões: um papel é uma lista de telas marcadas — é isso que o
// cartão mostra, com a primeira permissão já ligada (o acento).
export function RolesIllustration(props: ComponentProps<"svg">) {
  return (
    <Illustration groundY={106} groundRx={40} {...props}>
      <rect x="34" y="16" width="92" height="82" rx="10" className="fill-current opacity-[0.06]" />
      <rect
        x="34"
        y="16"
        width="92"
        height="82"
        rx="10"
        className="stroke-current opacity-25"
        strokeWidth="2"
      />
      <rect x="48" y="30" width="38" height="7" rx="3.5" className="fill-current opacity-30" />
      {[52, 68, 84].map((y, i) => (
        <g key={y}>
          <rect
            x="48"
            y={y}
            width="11"
            height="11"
            rx="3"
            className={i === 0 ? "stroke-current text-primary" : "stroke-current opacity-25"}
            strokeWidth="2"
          />
          <rect
            x="66"
            y={y + 3}
            width={i === 0 ? 46 : i === 1 ? 36 : 28}
            height="5"
            rx="2.5"
            className="fill-current opacity-20"
          />
        </g>
      ))}
      <path
        d="m50.5 57.5 2.5 2.5 4.5-4.5"
        className="stroke-current text-primary"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Illustration>
  );
}

// Espera entre ligações: duas ligações na linha do tempo, cada uma com a onda
// da conversa, e o vão tracejado entre elas. A ampulheta no vão é o acento —
// o relatório mede exatamente esse tempo parado entre desligar e ligar de novo.
export function WaitIllustration(props: ComponentProps<"svg">) {
  // Alturas das barras da onda: a conversa de cada ligação.
  const onda = [10, 18, 12, 24, 16, 8];
  const bloco = (x: number) => (
    <g key={x}>
      <rect x={x} y="40" width="46" height="40" rx="8" className="fill-current opacity-[0.06]" />
      <rect
        x={x}
        y="40"
        width="46"
        height="40"
        rx="8"
        className="stroke-current opacity-25"
        strokeWidth="2"
      />
      {onda.map((h, i) => (
        <rect
          key={i}
          x={x + 9 + i * 5}
          y={60 - h / 2}
          width="3"
          height={h}
          rx="1.5"
          className="fill-current opacity-30"
        />
      ))}
    </g>
  );
  return (
    <Illustration groundY={102} groundRx={58} {...props}>
      {bloco(12)}
      {bloco(102)}
      {/* O vão entre as ligações: tracejado, porque é tempo sem conversa. */}
      <path
        d="M62 60h36"
        className="stroke-current opacity-25"
        strokeWidth="2"
        strokeDasharray="3 4"
        strokeLinecap="round"
      />
      {/* A ampulheta sobre o vão: é o que o relatório mede. */}
      <path
        d="M70 32h20c0 12-7 18-7 28s7 16 7 28H70c0-12 7-18 7-28s-7-16-7-28z"
        className="fill-background stroke-current text-primary"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path d="M74.5 42h11L80 52z" className="fill-current text-primary opacity-40" />
      <path
        d="M73 86c1.5-6 4.5-9 7-9s5.5 3 7 9z"
        className="fill-current text-primary opacity-60"
      />
      <rect x="66" y="28" width="28" height="5" rx="2.5" className="fill-current text-primary" />
      <rect x="66" y="87" width="28" height="5" rx="2.5" className="fill-current text-primary" />
    </Illustration>
  );
}

// Automações: dois nós ligados por um gatilho. O nó de chegada é tracejado —
// é o pedaço que ainda não existe.
export function AutomationIllustration(props: ComponentProps<"svg">) {
  return (
    <Illustration groundY={88} groundRx={44} {...props}>
      <rect x="14" y="30" width="52" height="44" rx="10" className="fill-current opacity-[0.06]" />
      <rect
        x="14"
        y="30"
        width="52"
        height="44"
        rx="10"
        className="stroke-current opacity-25"
        strokeWidth="2"
      />
      <rect x="26" y="42" width="28" height="5" rx="2.5" className="fill-current opacity-30" />
      <rect x="26" y="54" width="18" height="5" rx="2.5" className="fill-current opacity-20" />
      <path
        d="M72 52h16"
        className="stroke-current opacity-25"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="m84 46 6 6-6 6"
        className="stroke-current opacity-25"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <rect
        x="98"
        y="30"
        width="52"
        height="44"
        rx="10"
        className="stroke-current text-primary"
        strokeWidth="2"
        strokeDasharray="6 5"
      />
      <path d="m127 38-10 15h8l-3 12 11-16h-8z" className="fill-current text-primary opacity-80" />
    </Illustration>
  );
}

// Visão geral da operação: o painel com os indicadores ainda fantasmas — os
// blocos de número existem, só falta ligação para preenchê-los. Embaixo, a
// linha da produção só tem o primeiro trecho: o ponto aceso é o acento,
// porque é a operação começando; o resto da linha, tracejado, ainda não
// aconteceu.
export function OperationOverviewIllustration(props: ComponentProps<"svg">) {
  return (
    <Illustration groundY={106} groundRx={46} {...props}>
      <rect x="20" y="14" width="120" height="84" rx="10" className="fill-current opacity-[0.06]" />
      <rect
        x="20"
        y="14"
        width="120"
        height="84"
        rx="10"
        className="stroke-current opacity-25"
        strokeWidth="2"
      />
      {/* Três blocos de indicador: rótulo e o número que ainda não veio. */}
      {[30, 65, 100].map((x) => (
        <g key={x}>
          <rect
            x={x}
            y="24"
            width="30"
            height="26"
            rx="5"
            className="stroke-current opacity-20"
            strokeWidth="1.5"
          />
          <rect x={x + 5} y="30" width="14" height="4" rx="2" className="fill-current opacity-20" />
          <rect
            x={x + 5}
            y="39"
            width="20"
            height="6"
            rx="3"
            className="fill-current opacity-[0.12]"
          />
        </g>
      ))}
      <path
        d="M30 88h100"
        className="stroke-current opacity-25"
        strokeWidth="2"
        strokeLinecap="round"
      />
      {/* O resto da produção: tracejado, porque ainda não aconteceu. */}
      <path
        d="M53 70l13 6 14-10 16 6 16-12 18 4"
        className="stroke-current opacity-25"
        strokeWidth="2"
        strokeDasharray="4 4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M37 79l16-9"
        className="stroke-current text-primary"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <circle
        cx="37"
        cy="79"
        r="5"
        className="fill-background stroke-current text-primary"
        strokeWidth="2"
      />
    </Illustration>
  );
}

// Financeiro: a fatura que ainda não fechou — recibo com serrilha embaixo e a
// linha do total em destaque.
export function InvoiceIllustration(props: ComponentProps<"svg">) {
  return (
    <Illustration groundY={102} groundRx={34} {...props}>
      <path
        d="M44 18a6 6 0 0 1 6-6h60a6 6 0 0 1 6 6v76l-9-6-9 6-9-6-9 6-9-6-9 6-9-6-9 6z"
        className="fill-current opacity-[0.06]"
      />
      <path
        d="M44 18a6 6 0 0 1 6-6h60a6 6 0 0 1 6 6v76l-9-6-9 6-9-6-9 6-9-6-9 6-9-6-9 6z"
        className="stroke-current opacity-25"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <rect x="56" y="28" width="34" height="6" rx="3" className="fill-current opacity-30" />
      {[46, 58].map((y) => (
        <g key={y}>
          <rect x="56" y={y} width="26" height="5" rx="2.5" className="fill-current opacity-20" />
          <rect x="88" y={y} width="16" height="5" rx="2.5" className="fill-current opacity-20" />
        </g>
      ))}
      <rect
        x="56"
        y="72"
        width="48"
        height="7"
        rx="3.5"
        className="fill-current text-primary opacity-70"
      />
    </Illustration>
  );
}

// Avaliações da IA: a transcrição da ligação — falas alternadas entre a IA e o
// lead — com o selo da nota no canto. A estrela é o acento: a avaliação só
// existe quando o operador dá a nota no wrap-up.
export function AiEvaluationIllustration(props: ComponentProps<"svg">) {
  return (
    <Illustration groundY={104} groundRx={44} {...props}>
      <rect x="26" y="14" width="96" height="80" rx="10" className="fill-current opacity-[0.06]" />
      <rect
        x="26"
        y="14"
        width="96"
        height="80"
        rx="10"
        className="stroke-current opacity-25"
        strokeWidth="2"
      />
      <rect x="38" y="26" width="32" height="6" rx="3" className="fill-current opacity-30" />
      {[
        { y: 42, w: 50 },
        { y: 54, w: 36 },
        { y: 66, w: 44 },
        { y: 78, w: 24 },
      ].map(({ y, w }, i) => (
        <g key={y}>
          <circle cx="41" cy={y + 2.5} r="3" className="fill-current opacity-25" />
          <rect
            x="48"
            y={y}
            width={w}
            height="5"
            rx="2.5"
            className={cn("fill-current", i % 2 === 0 ? "opacity-20" : "opacity-[0.15]")}
          />
        </g>
      ))}
      <circle
        cx="118"
        cy="78"
        r="18"
        className="fill-background stroke-current text-primary"
        strokeWidth="2"
      />
      <path
        d="M118 69l2.29 5.84 6.27.38-4.85 3.99 1.58 6.07L118 81.9l-5.29 3.38 1.58-6.07-4.85-3.99 6.27-.38z"
        className="fill-current stroke-current text-primary"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </Illustration>
  );
}

// Chaves de API: a chave sobre o cartão de credenciais. O anel é o acento —
// é o que a pessoa vai gerar.
export function ApiKeyIllustration(props: ComponentProps<"svg">) {
  return (
    <Illustration groundY={96} groundRx={40} {...props}>
      <rect x="34" y="26" width="92" height="62" rx="10" className="fill-current opacity-[0.06]" />
      <rect
        x="34"
        y="26"
        width="92"
        height="62"
        rx="10"
        className="stroke-current opacity-25"
        strokeWidth="2"
      />
      <rect x="46" y="38" width="30" height="5" rx="2.5" className="fill-current opacity-25" />
      <rect x="46" y="72" width="52" height="5" rx="2.5" className="fill-current opacity-20" />
      <circle cx="62" cy="58" r="10" className="stroke-current text-primary" strokeWidth="2.5" />
      <path
        d="M72 58h30m-8 0v8m-8-8v6"
        className="stroke-current text-primary"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </Illustration>
  );
}

// Troncos SIP: a ponte entre o servidor de telefonia e a operadora. O rack
// está pronto e a nuvem da operadora existe do outro lado — o que falta é o
// cabo entre os dois, tracejado no acento, porque cadastrar o tronco é
// justamente ligar um ao outro.
export function TrunkIllustration(props: ComponentProps<"svg">) {
  return (
    <Illustration groundY={104} groundRx={56} {...props}>
      <rect x="20" y="22" width="48" height="76" rx="8" className="fill-current opacity-[0.06]" />
      <rect
        x="20"
        y="22"
        width="48"
        height="76"
        rx="8"
        className="stroke-current opacity-25"
        strokeWidth="2"
      />
      {[34, 54, 74].map((y) => (
        <g key={y}>
          <rect
            x="29"
            y={y}
            width="30"
            height="12"
            rx="3"
            className="fill-current opacity-[0.12]"
          />
          <circle cx="35" cy={y + 6} r="2" className="fill-current opacity-35" />
          <rect
            x="41"
            y={y + 4.5}
            width="13"
            height="3"
            rx="1.5"
            className="fill-current opacity-25"
          />
        </g>
      ))}
      <path
        d="M108 66a12 12 0 0 1-1.5-23.9A18 18 0 0 1 140 38a14 14 0 0 1 2 28z"
        className="fill-current opacity-[0.06]"
      />
      <path
        d="M108 66a12 12 0 0 1-1.5-23.9A18 18 0 0 1 140 38a14 14 0 0 1 2 28z"
        className="stroke-current opacity-25"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <rect x="112" y="50" width="22" height="5" rx="2.5" className="fill-current opacity-25" />
      <path
        d="M68 80h42a8 8 0 0 0 8-8v-6"
        className="stroke-current text-primary"
        strokeWidth="2"
        strokeLinecap="round"
        strokeDasharray="5 5"
      />
      <circle cx="118" cy="66" r="3" className="fill-current text-primary" />
    </Illustration>
  );
}

// Suporte: a conversa que ainda não começou. O balão da resposta é o acento —
// a promessa de que alguém responde.
export function TicketIllustration(props: ComponentProps<"svg">) {
  return (
    <Illustration groundY={104} groundRx={38} {...props}>
      <path
        d="M28 26a8 8 0 0 1 8-8h56a8 8 0 0 1 8 8v30a8 8 0 0 1-8 8H52l-16 12V64h-.5a8 8 0 0 1-7.5-8z"
        className="fill-current opacity-[0.06]"
      />
      <path
        d="M28 26a8 8 0 0 1 8-8h56a8 8 0 0 1 8 8v30a8 8 0 0 1-8 8H52l-16 12V64h-.5a8 8 0 0 1-7.5-8z"
        className="stroke-current opacity-25"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <rect x="44" y="32" width="40" height="5" rx="2.5" className="fill-current opacity-25" />
      <rect x="44" y="44" width="26" height="5" rx="2.5" className="fill-current opacity-20" />
      <path
        d="M112 52h20a6 6 0 0 1 6 6v20a6 6 0 0 1-6 6h-4v10l-12-10h-4a6 6 0 0 1-6-6V58a6 6 0 0 1 6-6z"
        className="stroke-current text-primary"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path
        d="M120 64h12m-12 10h7"
        className="stroke-current text-primary opacity-60"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </Illustration>
  );
}

// Logs de webhook: a lista das requisições que chegaram, cada linha com o
// status, o evento e a hora — aqui fantasmas, porque nada chegou no recorte.
// O envelope entrando pelo canto é o acento: a tela só tem o que mostrar
// quando alguma integração manda um payload para cá.
export function WebhookLogIllustration(props: ComponentProps<"svg">) {
  return (
    <Illustration groundY={108} groundRx={44} {...props}>
      <rect x="16" y="24" width="108" height="78" rx="10" className="fill-current opacity-[0.06]" />
      <rect
        x="16"
        y="24"
        width="108"
        height="78"
        rx="10"
        className="stroke-current opacity-25"
        strokeWidth="2"
      />
      {[46, 66, 86].map((y, i) => (
        <g key={y}>
          <rect x="28" y={y - 4} width="16" height="8" rx="4" className="fill-current opacity-25" />
          <rect
            x="50"
            y={y - 2.5}
            width={i === 0 ? 38 : i === 1 ? 30 : 24}
            height="5"
            rx="2.5"
            className="fill-current opacity-20"
          />
          <rect x="96" y={y - 2} width="16" height="4" rx="2" className="fill-current opacity-15" />
        </g>
      ))}
      <path d="M28 56h84M28 76h84" className="stroke-current opacity-[0.12]" strokeWidth="1.5" />
      <rect
        x="96"
        y="8"
        width="40"
        height="28"
        rx="5"
        className="fill-background stroke-current text-primary"
        strokeWidth="2"
      />
      <path
        d="m100 13 16 12 16-12"
        className="stroke-current text-primary"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M140 15h8M142 22h8M140 29h8"
        className="stroke-current text-primary opacity-60"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </Illustration>
  );
}

// Monitoramento ao vivo: duas pontas de uma ligação unidas pela onda de áudio.
// O ponto aceso é a chamada que está acontecendo agora.
export function ActiveCallsIllustration(props: ComponentProps<"svg">) {
  return (
    <Illustration groundY={104} groundRx={44} {...props}>
      <rect x="18" y="31" width="43" height="54" rx="10" className="fill-current opacity-[0.06]" />
      <rect
        x="18"
        y="31"
        width="43"
        height="54"
        rx="10"
        className="stroke-current opacity-25"
        strokeWidth="2"
      />
      <circle cx="39.5" cy="49" r="8" className="fill-current opacity-20" />
      <rect x="29" y="64" width="21" height="5" rx="2.5" className="fill-current opacity-25" />
      <rect x="25" y="75" width="29" height="4" rx="2" className="fill-current opacity-15" />

      <rect x="99" y="31" width="43" height="54" rx="10" className="fill-current opacity-[0.06]" />
      <rect
        x="99"
        y="31"
        width="43"
        height="54"
        rx="10"
        className="stroke-current opacity-25"
        strokeWidth="2"
      />
      <circle cx="120.5" cy="49" r="8" className="fill-current opacity-20" />
      <rect x="110" y="64" width="21" height="5" rx="2.5" className="fill-current opacity-25" />
      <rect x="106" y="75" width="29" height="4" rx="2" className="fill-current opacity-15" />

      <path
        d="M66 58h5l3-9 5 20 5-27 5 21 3-5h3"
        className="stroke-current text-primary"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="80" cy="20" r="5" className="fill-current text-primary opacity-80" />
      <circle
        cx="80"
        cy="20"
        r="10"
        className="stroke-current text-primary opacity-25"
        strokeWidth="2"
      />
    </Illustration>
  );
}

// Custos das ligações: o gráfico do custo por dia ainda fantasma — sem
// ligação no período não há o que somar — e, na frente dele, a pilha de
// moedas que ele vai contar. A moeda em pé, com o cifrão, é o acento: o que
// esta tela responde é quanto as ligações custaram.
export function CostsIllustration(props: ComponentProps<"svg">) {
  return (
    <Illustration groundY={106} groundRx={50} {...props}>
      <rect x="14" y="16" width="96" height="70" rx="10" className="fill-current opacity-[0.06]" />
      <rect
        x="14"
        y="18"
        width="96"
        height="70"
        rx="10"
        className="stroke-current opacity-25"
        strokeWidth="2"
      />
      <path
        d="M28 30v44h66"
        className="stroke-current opacity-25"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <rect x="38" y="58" width="9" height="16" rx="3" className="fill-current opacity-[0.12]" />
      <rect x="53" y="46" width="9" height="28" rx="3" className="fill-current opacity-[0.12]" />
      <rect x="68" y="54" width="9" height="20" rx="3" className="fill-current opacity-[0.12]" />
      {/* Moedas de baixo para cima: cada uma cobre a borda da de baixo. */}
      {[93, 84, 75].map((cy) => {
        const side = `M86 ${cy}v6a20 6.5 0 0 0 40 0v-6a20 6.5 0 0 1-40 0z`;
        return (
          <g key={cy}>
            <path d={side} className="fill-background" />
            <path d={side} className="fill-current opacity-[0.12]" />
            <path
              d={side}
              className="stroke-current opacity-25"
              strokeWidth="2"
              strokeLinejoin="round"
            />
            <ellipse cx="106" cy={cy} rx="20" ry="6.5" className="fill-background" />
            <ellipse cx="106" cy={cy} rx="20" ry="6.5" className="fill-current opacity-[0.06]" />
            <ellipse
              cx="106"
              cy={cy}
              rx="20"
              ry="6.5"
              className="stroke-current opacity-25"
              strokeWidth="2"
            />
          </g>
        );
      })}
      <circle
        cx="134"
        cy="84"
        r="17"
        className="fill-background stroke-current text-primary"
        strokeWidth="2.5"
      />
      <path
        d="M138.5 79c-.8-1.4-2.4-2.2-4.3-2.2-2.5 0-4.2 1.4-4.2 3.3 0 4.6 8.6 2.6 8.6 7.6 0 2-1.9 3.5-4.5 3.5-2 0-3.6-.9-4.5-2.3M134 73.5v3.3m0 14.2v3.5"
        className="stroke-current text-primary"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Illustration>
  );
}

// Filtro sem resultado — o oposto do vazio de verdade: a lista existe, o
// recorte é que não achou. A lupa está SOBRE as linhas, não no lugar delas.
export function SearchEmptyIllustration(props: ComponentProps<"svg">) {
  return (
    <Illustration groundY={106} groundRx={40} {...props}>
      <rect x="30" y="20" width="100" height="72" rx="10" className="fill-current opacity-[0.06]" />
      <rect
        x="30"
        y="20"
        width="100"
        height="72"
        rx="10"
        className="stroke-current opacity-25"
        strokeWidth="2"
      />
      {[34, 50, 66].map((y, i) => (
        <rect
          key={y}
          x="44"
          y={y}
          width={i === 1 ? 44 : 56}
          height="6"
          rx="3"
          className="fill-current opacity-[0.15]"
        />
      ))}
      <circle
        cx="98"
        cy="70"
        r="19"
        className="fill-background stroke-current text-primary"
        strokeWidth="2.5"
      />
      <path
        d="m112 84 12 12"
        className="stroke-current text-primary"
        strokeWidth="3"
        strokeLinecap="round"
      />
      <path
        d="m92 64 12 12m0-12-12 12"
        className="stroke-current text-primary opacity-60"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </Illustration>
  );
}

// Quadro de leads: as etapas lado a lado, cada coluna com cartões fantasmas —
// o quadro existe, só não tem ninguém ali ainda. O acento é o cartão que está
// sendo arrastado para a etapa seguinte, com a vaga tracejada esperando por
// ele: o quadro só serve para isso, andar com o lead de uma etapa para outra.
export function LeadBoardIllustration(props: ComponentProps<"svg">) {
  const colunas = [
    { x: 26, cartoes: [38] },
    { x: 63, cartoes: [38] },
    { x: 100, cartoes: [38, 54] },
  ];
  return (
    <Illustration groundY={104} groundRx={50} {...props}>
      <rect x="18" y="18" width="124" height="78" rx="10" className="fill-current opacity-[0.06]" />
      <rect
        x="18"
        y="18"
        width="124"
        height="78"
        rx="10"
        className="stroke-current opacity-25"
        strokeWidth="2"
      />
      {colunas.map((coluna) => (
        <g key={coluna.x}>
          <rect
            x={coluna.x}
            y="26"
            width="34"
            height="62"
            rx="6"
            className="fill-current opacity-[0.06]"
          />
          <rect
            x={coluna.x + 4}
            y="30"
            width="16"
            height="4"
            rx="2"
            className="fill-current opacity-30"
          />
          {coluna.cartoes.map((y) => (
            <g key={y}>
              <rect
                x={coluna.x + 4}
                y={y}
                width="26"
                height="12"
                rx="3"
                className="stroke-current opacity-25"
                strokeWidth="2"
              />
              <rect
                x={coluna.x + 8}
                y={y + 4.5}
                width="14"
                height="3"
                rx="1.5"
                className="fill-current opacity-20"
              />
            </g>
          ))}
        </g>
      ))}
      <rect
        x="67"
        y="54"
        width="26"
        height="12"
        rx="3"
        className="stroke-current opacity-25"
        strokeWidth="2"
        strokeDasharray="4 3"
      />
      <g transform="rotate(-8 64 66)">
        <rect
          x="51"
          y="60"
          width="26"
          height="12"
          rx="3"
          className="fill-background stroke-current text-primary"
          strokeWidth="2"
        />
        <rect
          x="55"
          y="64.5"
          width="14"
          height="3"
          rx="1.5"
          className="fill-current text-primary opacity-60"
        />
      </g>
    </Illustration>
  );
}

// Áudios de atendimento: o objeto é a gravação que toca no começo da ligação —
// o cartão do arquivo com a onda do som. O play é o acento: o áudio existe
// para ser ouvido, e é a primeira coisa que se faz com ele aqui.
export function GreetingAudioIllustration(props: ComponentProps<"svg">) {
  const barras = [
    { x: 74, h: 16 },
    { x: 82, h: 28 },
    { x: 90, h: 38 },
    { x: 98, h: 22 },
    { x: 106, h: 32 },
    { x: 114, h: 14 },
  ];
  return (
    <Illustration groundY={102} groundRx={42} {...props}>
      <rect x="22" y="26" width="116" height="62" rx="12" className="fill-current opacity-[0.06]" />
      <rect
        x="22"
        y="26"
        width="116"
        height="62"
        rx="12"
        className="stroke-current opacity-25"
        strokeWidth="2"
      />
      <rect x="42" y="38" width="42" height="6" rx="3" className="fill-current opacity-30" />
      {barras.map((b) => (
        <rect
          key={b.x}
          x={b.x}
          y={66 - b.h / 2}
          width="4"
          height={b.h}
          rx="2"
          className="fill-current opacity-25"
        />
      ))}
      <circle
        cx="52"
        cy="66"
        r="11"
        className="fill-background stroke-current text-primary"
        strokeWidth="2"
      />
      <path d="m49.5 61 7.5 5-7.5 5z" className="fill-current text-primary" />
    </Illustration>
  );
}

// Pagamentos: o cartão da cobrança, ainda fantasma — chip, número e titular
// esmaecidos porque nenhum mês foi cobrado. O selo de pago na quina é o
// acento: é o que cada linha da tabela vai mostrar quando a fatura chegar.
// (Não é o recibo da `InvoiceIllustration`: aqui o objeto é o pagamento, não
// a conta.)
export function PaymentIllustration(props: ComponentProps<"svg">) {
  return (
    <Illustration groundY={104} groundRx={44} {...props}>
      <rect x="26" y="22" width="100" height="64" rx="10" className="fill-current opacity-[0.06]" />
      <rect
        x="26"
        y="22"
        width="100"
        height="64"
        rx="10"
        className="stroke-current opacity-25"
        strokeWidth="2"
      />
      <rect
        x="38"
        y="34"
        width="18"
        height="13"
        rx="3"
        className="stroke-current opacity-30"
        strokeWidth="2"
      />
      <path d="M38 40.5h18M47 34v13" className="stroke-current opacity-20" strokeWidth="1.5" />
      {[38, 58, 78].map((x) => (
        <rect
          key={x}
          x={x}
          y="58"
          width="16"
          height="5"
          rx="2.5"
          className="fill-current opacity-25"
        />
      ))}
      <rect x="38" y="70" width="30" height="5" rx="2.5" className="fill-current opacity-20" />
      <circle
        cx="120"
        cy="80"
        r="16"
        className="fill-background stroke-current text-primary"
        strokeWidth="2.5"
      />
      <path
        d="m112.5 80 5 5 10-10"
        className="stroke-current text-primary"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Illustration>
  );
}

// Formulário pós-ligação: o que o vendedor preenche quando desliga. A ficha
// mostra as duas coisas que ele responde ali — um campo escrito e a escolha
// de um desfecho — e o botão de salvar é o acento: o formulário só serve
// quando alguém o responde até o fim.
export function CallFormIllustration(props: ComponentProps<"svg">) {
  return (
    <Illustration groundY={112} groundRx={38} {...props}>
      <rect x="34" y="10" width="92" height="96" rx="10" className="fill-current opacity-[0.06]" />
      <rect
        x="34"
        y="10"
        width="92"
        height="96"
        rx="10"
        className="stroke-current opacity-25"
        strokeWidth="2"
      />
      <rect x="48" y="24" width="38" height="6" rx="3" className="fill-current opacity-30" />

      <rect
        x="48"
        y="36"
        width="64"
        height="14"
        rx="4"
        className="stroke-current opacity-25"
        strokeWidth="2"
      />
      <rect x="54" y="41" width="30" height="4" rx="2" className="fill-current opacity-20" />

      {/* A escolha do desfecho: a primeira opção já marcada, porque a ficha
          nunca chega em branco ao vendedor. */}
      <circle cx="52" cy="62" r="4.5" className="fill-current opacity-35" />
      <rect x="62" y="60" width="32" height="4" rx="2" className="fill-current opacity-25" />
      <circle cx="52" cy="76" r="4.5" className="stroke-current opacity-25" strokeWidth="2" />
      <rect x="62" y="74" width="22" height="4" rx="2" className="fill-current opacity-[0.15]" />

      <rect x="48" y="86" width="44" height="14" rx="7" className="fill-current text-primary" />
      <rect x="58" y="91" width="24" height="4" rx="2" className="fill-background" />
    </Illustration>
  );
}

// Consentimentos e apagamentos (LGPD): o registro do titular é uma folha com o
// texto do termo e a linha da assinatura. O escudo com o check é o acento
// porque o que se faz ali é deixar a base legal registrada e protegida; ele sai
// da folha como o relógio sai da grade no horário de atendimento.
export function ConsentIllustration(props: ComponentProps<"svg">) {
  return (
    <Illustration groundY={108} groundRx={40} {...props}>
      <rect x="31" y="12" width="76" height="90" rx="10" className="fill-current opacity-[0.06]" />
      <rect
        x="31"
        y="12"
        width="76"
        height="90"
        rx="10"
        className="stroke-current opacity-25"
        strokeWidth="2"
      />
      <rect x="43" y="24" width="36" height="6" rx="3" className="fill-current opacity-30" />
      {[
        { y: 38, w: 52 },
        { y: 47, w: 46 },
        { y: 56, w: 50 },
        { y: 65, w: 30 },
      ].map((l) => (
        <rect
          key={l.y}
          x="43"
          y={l.y}
          width={l.w}
          height="4"
          rx="2"
          className="fill-current opacity-20"
        />
      ))}
      {/* A linha da assinatura: o termo só vale depois que o titular aceita. */}
      <path
        d="M43 88c4-6 7-6 9 0s5 5 8-1"
        className="stroke-current opacity-35"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M43 94h28"
        className="stroke-current opacity-25"
        strokeWidth="2"
        strokeLinecap="round"
      />

      <path
        d="M109 56l20 7v15c0 13-9 21-20 25c-11-4-20-12-20-25v-15z"
        className="fill-background stroke-current text-primary"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path
        d="m100.5 79 6 6 11-12"
        className="stroke-current text-primary"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Illustration>
  );
}

// Horário de atendimento: a semana desenhada como faixas de tempo, uma por dia,
// com o relógio na frente. As faixas curtas no fim são o fim de semana; o
// relógio é o acento porque a decisão ali é declarar as horas em que a marca
// atende.
export function BusinessHoursIllustration(props: ComponentProps<"svg">) {
  const faixas = [
    { y: 30, x: 46, w: 62 },
    { y: 42, x: 46, w: 62 },
    { y: 54, x: 46, w: 58 },
    { y: 66, x: 46, w: 62 },
    { y: 78, x: 46, w: 50 },
  ];
  return (
    <Illustration groundY={108} groundRx={40} {...props}>
      <rect x="20" y="14" width="120" height="82" rx="10" className="fill-current opacity-[0.06]" />
      <rect
        x="20"
        y="14"
        width="120"
        height="82"
        rx="10"
        className="stroke-current opacity-25"
        strokeWidth="2"
      />
      {/* Coluna dos dias e a faixa de horas de cada um. */}
      {faixas.map((f) => (
        <g key={f.y}>
          <rect x="30" y={f.y} width="10" height="6" rx="3" className="fill-current opacity-25" />
          <rect
            x={f.x}
            y={f.y}
            width={f.w}
            height="6"
            rx="3"
            className="fill-current opacity-[0.18]"
          />
        </g>
      ))}
      <rect x="30" y="90" width="10" height="6" rx="3" className="fill-current opacity-[0.12]" />

      {/* O relógio sai da moldura: é ele que manda na grade toda. */}
      <circle
        cx="112"
        cy="82"
        r="20"
        className="fill-background stroke-current text-primary"
        strokeWidth="2"
      />
      <path
        d="M112 70v12l8 5"
        className="stroke-current text-primary"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Illustration>
  );
}

// Nenhuma falha no período: a lista de ligações com todas as linhas marcadas —
// nenhuma ficou pelo caminho. O selo com o check no canto é o acento: aqui o
// vazio é a notícia boa, não um dado faltando.
export function NoFailuresIllustration(props: ComponentProps<"svg">) {
  return (
    <Illustration groundY={104} groundRx={44} {...props}>
      <rect x="26" y="14" width="96" height="80" rx="10" className="fill-current opacity-[0.06]" />
      <rect
        x="26"
        y="14"
        width="96"
        height="80"
        rx="10"
        className="stroke-current opacity-25"
        strokeWidth="2"
      />
      <rect x="38" y="26" width="32" height="6" rx="3" className="fill-current opacity-30" />
      {[
        { y: 42, w: 46 },
        { y: 56, w: 36 },
        { y: 70, w: 42 },
      ].map(({ y, w }) => (
        <g key={y}>
          <circle
            cx="42"
            cy={y + 3}
            r="4.5"
            className="stroke-current opacity-30"
            strokeWidth="1.5"
          />
          <path
            d={`M39.8 ${y + 3}l1.6 1.6 3-3.2`}
            className="stroke-current opacity-35"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <rect
            x="52"
            y={y + 0.5}
            width={w}
            height="5"
            rx="2.5"
            className="fill-current opacity-20"
          />
        </g>
      ))}
      <path
        d="M118 54l18 6.5v11c0 11.5-7.7 19.8-18 23.5-10.3-3.7-18-12-18-23.5v-11z"
        className="fill-background stroke-current text-primary"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path
        d="M110 74l5.5 5.5 10-10.5"
        className="stroke-current text-primary"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Illustration>
  );
}

// Relatórios sem dado no período: as barras existem, mas fantasmas — o que
// falta é dado, não configuração. O acento é o filtro de período, que é
// justamente o que a pessoa precisa mexer para ver alguma coisa.
export function NoDataIllustration(props: ComponentProps<"svg">) {
  return (
    <Illustration groundY={106} groundRx={44} {...props}>
      <rect x="22" y="20" width="116" height="72" rx="10" className="fill-current opacity-[0.06]" />
      <rect
        x="22"
        y="20"
        width="116"
        height="72"
        rx="10"
        className="stroke-current opacity-25"
        strokeWidth="2"
      />
      <rect
        x="96"
        y="28"
        width="30"
        height="12"
        rx="6"
        className="stroke-current text-primary"
        strokeWidth="2"
      />
      <rect
        x="102"
        y="32.75"
        width="18"
        height="3.5"
        rx="1.75"
        className="fill-current text-primary opacity-60"
      />
      <path
        d="M40 32v46h80"
        className="stroke-current opacity-25"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <rect x="50" y="62" width="10" height="16" rx="3" className="fill-current opacity-[0.12]" />
      <rect x="66" y="52" width="10" height="26" rx="3" className="fill-current opacity-[0.12]" />
      <rect x="82" y="66" width="10" height="12" rx="3" className="fill-current opacity-[0.12]" />
      <rect x="98" y="58" width="10" height="20" rx="3" className="fill-current opacity-[0.12]" />
    </Illustration>
  );
}

// Disponibilidade ao longo do dia: uma linha do tempo por operador, sob a
// régua das horas. As faixas são fantasmas porque o período ainda não tem
// plantão registrado; a única acesa, em text-primary, é o trecho "disponível" —
// é esse tempo de plantão que a tela existe para medir.
export function AvailabilityTimelineIllustration(props: ComponentProps<"svg">) {
  const linhas = [
    { y: 44, faixas: [{ x: 54, w: 16 }] },
    {
      y: 62,
      faixas: [
        { x: 54, w: 26 },
        { x: 94, w: 22 },
      ],
    },
    { y: 80, faixas: [{ x: 72, w: 30 }] },
  ];
  return (
    <Illustration groundY={106} groundRx={44} {...props}>
      <rect x="20" y="16" width="120" height="80" rx="10" className="fill-current opacity-[0.06]" />
      <rect
        x="20"
        y="16"
        width="120"
        height="80"
        rx="10"
        className="stroke-current opacity-25"
        strokeWidth="2"
      />
      {/* Régua das horas: marcas curtas no topo, alinhadas ao trilho. */}
      {[54, 70, 86, 102, 118].map((x) => (
        <rect
          key={x}
          x={x - 1}
          y="26"
          width="2"
          height="6"
          rx="1"
          className="fill-current opacity-25"
        />
      ))}
      {/* Cada operador: o avatar, o trilho do expediente e as faixas fantasmas. */}
      {linhas.map((l) => (
        <g key={l.y}>
          <circle cx="36" cy={l.y + 4} r="6" className="fill-current opacity-25" />
          <rect
            x="50"
            y={l.y}
            width="78"
            height="8"
            rx="4"
            className="fill-current opacity-[0.08]"
          />
          {l.faixas.map((f) => (
            <rect
              key={f.x}
              x={f.x}
              y={l.y}
              width={f.w}
              height="8"
              rx="4"
              className="fill-current opacity-20"
            />
          ))}
        </g>
      ))}
      {/* O trecho disponível: o plantão que a tela mede. */}
      <rect x="76" y="44" width="40" height="8" rx="4" className="fill-current text-primary" />
    </Illustration>
  );
}

// Leads: a lista de quem ainda não existe para o discador. Cada linha é uma
// pessoa com telefone — e o acento é o handset da primeira, porque a única
// coisa que se faz com um lead aqui é ligar para ele.
export function LeadsIllustration(props: ComponentProps<"svg">) {
  return (
    <Illustration groundY={108} groundRx={42} {...props}>
      <rect x="26" y="16" width="108" height="86" rx="10" className="fill-current opacity-[0.06]" />
      <rect
        x="26"
        y="16"
        width="108"
        height="86"
        rx="10"
        className="stroke-current opacity-25"
        strokeWidth="2"
      />
      <circle cx="46" cy="36" r="7" className="fill-current opacity-25" />
      <rect x="60" y="33" width="40" height="6" rx="3" className="fill-current opacity-20" />
      <circle cx="46" cy="59" r="7" className="fill-current opacity-25" />
      <rect x="60" y="56" width="32" height="6" rx="3" className="fill-current opacity-[0.15]" />
      <circle cx="46" cy="82" r="7" className="fill-current opacity-25" />
      <rect x="60" y="79" width="24" height="6" rx="3" className="fill-current opacity-[0.15]" />
      <circle cx="113" cy="36" r="12" className="stroke-current text-primary" strokeWidth="2" />
      <path
        d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"
        className="stroke-current text-primary"
        transform="translate(106 29) scale(0.58)"
        strokeWidth="3.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Illustration>
  );
}

// Auditoria do preditivo: o discador liga para vários leads ao mesmo tempo e
// só a ligação atendida chega ao operador. As linhas de cima e de baixo,
// tracejadas, são as tentativas que ficaram pelo caminho; a do meio, cheia e
// no acento, é a que foi atendida e caiu no fone dele — cada uma dessas
// tentativas, atendida ou não, é uma linha da auditoria.
export function PredictiveAuditIllustration(props: ComponentProps<"svg">) {
  return (
    <Illustration groundY={102} groundRx={52} {...props}>
      {[18, 49, 80].map((y) => (
        <g key={y}>
          <rect
            x="12"
            y={y}
            width="40"
            height="20"
            rx="6"
            className="fill-current opacity-[0.06]"
          />
          <rect
            x="12"
            y={y}
            width="40"
            height="20"
            rx="6"
            className="stroke-current opacity-25"
            strokeWidth="2"
          />
          <circle cx="23" cy={y + 10} r="4" className="fill-current opacity-25" />
          <rect x="31" y={y + 8} width="15" height="4" rx="2" className="fill-current opacity-20" />
        </g>
      ))}

      <path
        d="M52 28c16 0 22 10 30 19"
        className="stroke-current opacity-25"
        strokeWidth="2"
        strokeDasharray="3 4"
        strokeLinecap="round"
      />
      <path
        d="M52 90c16 0 22-10 30-19"
        className="stroke-current opacity-25"
        strokeWidth="2"
        strokeDasharray="3 4"
        strokeLinecap="round"
      />

      <circle cx="124" cy="59" r="24" className="fill-current opacity-[0.06]" />
      <circle cx="124" cy="59" r="24" className="stroke-current opacity-25" strokeWidth="2" />
      <circle cx="124" cy="53" r="7" className="fill-current opacity-25" />
      <path d="M111 76c2-8 7-11 13-11s11 3 13 11" className="fill-current opacity-20" />
      <path
        d="M114 54a10 10 0 0 1 20 0"
        className="stroke-current opacity-35"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M134 56v3c0 3-2 5-5 5h-3"
        className="stroke-current opacity-35"
        strokeWidth="2"
        strokeLinecap="round"
      />

      <path
        d="M52 59h44"
        className="stroke-current text-primary"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
      <circle cx="100" cy="59" r="4" className="fill-current text-primary" />
    </Illustration>
  );
}

// Usuários: a equipe da marca. Duas pessoas já dentro e a terceira em
// tracejado com o mais — o convite que ainda não foi feito é o acento.
export function TeamIllustration(props: ComponentProps<"svg">) {
  return (
    <Illustration groundY={92} groundRx={44} {...props}>
      <circle cx="54" cy="44" r="11" className="stroke-current opacity-25" strokeWidth="2" />
      <path
        d="M36 74c0-10 8-18 18-18s18 8 18 18"
        className="stroke-current opacity-25"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <circle cx="86" cy="44" r="11" className="stroke-current opacity-25" strokeWidth="2" />
      <path
        d="M68 74c0-10 8-18 18-18s18 8 18 18"
        className="stroke-current opacity-25"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <circle
        cx="120"
        cy="52"
        r="14"
        className="stroke-current text-primary"
        strokeWidth="2"
        strokeDasharray="5 4"
      />
      <path
        d="M120 46v12m-6-6h12"
        className="stroke-current text-primary"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </Illustration>
  );
}

// Relatório da fila: a agenda das ligações — cada linha é um horário com o
// telefone de quem vai ser ligado, ainda fantasmas porque não há ninguém no
// recorte. O horário da primeira linha é o acento: a tabela é lida de cima
// para baixo, e o que ela responde é quem vem a seguir.
const QUEUE_SCHEDULE_PHONE =
  "M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z";

export function QueueScheduleIllustration(props: ComponentProps<"svg">) {
  return (
    <Illustration groundY={110} groundRx={46} {...props}>
      <rect x="28" y="10" width="104" height="94" rx="10" className="fill-current opacity-[0.06]" />
      <rect
        x="28"
        y="10"
        width="104"
        height="94"
        rx="10"
        className="stroke-current opacity-25"
        strokeWidth="2"
      />
      <rect x="40" y="21" width="30" height="6" rx="3" className="fill-current opacity-30" />
      <path
        d="M28 34h104M38 60h84M38 80h84"
        className="stroke-current opacity-[0.12]"
        strokeWidth="1.5"
      />
      {[50, 70, 90].map((y, i) => (
        <g key={y}>
          <rect
            x="38"
            y={y - 6}
            width="24"
            height="12"
            rx="6"
            className={i === 0 ? "stroke-current text-primary" : "stroke-current opacity-25"}
            strokeWidth="2"
          />
          <rect
            x="44"
            y={y - 1.5}
            width="12"
            height="3"
            rx="1.5"
            className={i === 0 ? "fill-current text-primary" : "fill-current opacity-25"}
          />
          <path
            d={QUEUE_SCHEDULE_PHONE}
            transform={`translate(69 ${y - 5}) scale(0.42)`}
            className="stroke-current opacity-25"
            strokeWidth="4.5"
            strokeLinejoin="round"
          />
          <rect
            x="86"
            y={y - 2.5}
            width={i === 0 ? 34 : i === 1 ? 28 : 22}
            height="5"
            rx="2.5"
            className="fill-current opacity-20"
          />
        </g>
      ))}
    </Illustration>
  );
}

// Filas: os leads em ordem de ligação. A seta e o contorno marcam quem é o
// próximo — a fila só significa alguma coisa por causa dessa ordem.
export function QueueIllustration(props: ComponentProps<"svg">) {
  return (
    <Illustration groundY={106} groundRx={40} {...props}>
      <rect x="46" y="22" width="78" height="20" rx="6" className="fill-current opacity-[0.06]" />
      <rect
        x="46"
        y="22"
        width="78"
        height="20"
        rx="6"
        className="stroke-current text-primary"
        strokeWidth="2"
      />
      <rect x="58" y="29" width="34" height="6" rx="3" className="fill-current opacity-25" />
      <rect x="46" y="50" width="78" height="20" rx="6" className="fill-current opacity-[0.06]" />
      <rect
        x="46"
        y="50"
        width="78"
        height="20"
        rx="6"
        className="stroke-current opacity-25"
        strokeWidth="2"
      />
      <rect x="58" y="57" width="28" height="6" rx="3" className="fill-current opacity-20" />
      <rect x="46" y="78" width="78" height="20" rx="6" className="fill-current opacity-[0.06]" />
      <rect
        x="46"
        y="78"
        width="78"
        height="20"
        rx="6"
        className="stroke-current opacity-25"
        strokeWidth="2"
      />
      <rect x="58" y="85" width="22" height="6" rx="3" className="fill-current opacity-[0.15]" />
      <path
        d="M24 32h12m-5-5 5 5-5 5"
        className="stroke-current text-primary"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Illustration>
  );
}

// Relatório geral das marcas: as marcas lado a lado, cada cartão com a
// inicial, o nome, as colunas do período e a barra da taxa de sucesso — tudo
// fantasma, porque nenhuma ligação entrou no recorte. A do meio está em
// destaque, e a barra da taxa dela é o acento: é o número que esta tela põe
// uma marca contra a outra.
export function BrandsReportIllustration(props: ComponentProps<"svg">) {
  const cartoes = [
    { x: 14, y: 36, w: 38, h: 60, barras: [10, 16, 12], taxa: 10 },
    { x: 58, y: 24, w: 44, h: 72, barras: [14, 24, 18], taxa: 22 },
    { x: 108, y: 36, w: 38, h: 60, barras: [8, 12, 16], taxa: 14 },
  ];
  return (
    <Illustration groundY={104} groundRx={62} {...props}>
      {cartoes.map((c, i) => {
        const destaque = i === 1;
        const base = c.y + c.h - 16;
        const larguraBarra = destaque ? 6 : 5;
        return (
          <g key={c.x}>
            <rect
              x={c.x}
              y={c.y}
              width={c.w}
              height={c.h}
              rx="8"
              className="fill-current opacity-[0.06]"
            />
            <rect
              x={c.x}
              y={c.y}
              width={c.w}
              height={c.h}
              rx="8"
              className="stroke-current opacity-25"
              strokeWidth="2"
            />
            <rect
              x={c.x + 6}
              y={c.y + 7}
              width={destaque ? 11 : 9}
              height={destaque ? 11 : 9}
              rx="3"
              className="fill-current opacity-25"
            />
            <rect
              x={c.x + (destaque ? 21 : 19)}
              y={c.y + (destaque ? 10.5 : 9.5)}
              width={destaque ? 17 : 13}
              height="4"
              rx="2"
              className="fill-current opacity-30"
            />
            {c.barras.map((h, j) => (
              <rect
                key={j}
                x={c.x + 7 + j * (larguraBarra + (destaque ? 5 : 3.5))}
                y={base - h}
                width={larguraBarra}
                height={h}
                rx="2"
                className="fill-current opacity-[0.15]"
              />
            ))}
            <rect
              x={c.x + 6}
              y={base + 6}
              width={c.w - 12}
              height={destaque ? 4 : 3}
              rx={destaque ? 2 : 1.5}
              className="fill-current opacity-[0.12]"
            />
            <rect
              x={c.x + 6}
              y={base + 6}
              width={c.taxa}
              height={destaque ? 4 : 3}
              rx={destaque ? 2 : 1.5}
              className={destaque ? "fill-current text-primary" : "fill-current opacity-25"}
            />
          </g>
        );
      })}
    </Illustration>
  );
}

// Ramais: o aparelho provisionado. Os arcos são o acento — um ramal só
// serve depois de registrado, e é isso que o sinal diz.
export function ExtensionIllustration(props: ComponentProps<"svg">) {
  return (
    <Illustration groundY={108} groundRx={32} {...props}>
      <rect x="50" y="22" width="54" height="78" rx="10" className="fill-current opacity-[0.06]" />
      <rect
        x="50"
        y="22"
        width="54"
        height="78"
        rx="10"
        className="stroke-current opacity-25"
        strokeWidth="2"
      />
      <rect x="60" y="32" width="34" height="14" rx="4" className="fill-current opacity-[0.15]" />
      <circle cx="64" cy="60" r="3.5" className="fill-current opacity-25" />
      <circle cx="77" cy="60" r="3.5" className="fill-current opacity-25" />
      <circle cx="90" cy="60" r="3.5" className="fill-current opacity-25" />
      <circle cx="64" cy="72" r="3.5" className="fill-current opacity-25" />
      <circle cx="77" cy="72" r="3.5" className="fill-current opacity-25" />
      <circle cx="90" cy="72" r="3.5" className="fill-current opacity-25" />
      <circle cx="64" cy="84" r="3.5" className="fill-current opacity-25" />
      <circle cx="77" cy="84" r="3.5" className="fill-current opacity-25" />
      <circle cx="90" cy="84" r="3.5" className="fill-current opacity-25" />
      <path
        d="M112 40a16 16 0 0 1 0 22"
        className="stroke-current text-primary"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M120 32a26 26 0 0 1 0 38"
        className="stroke-current text-primary"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </Illustration>
  );
}

// Assistente: a conversa que ainda não começou. Em cima, o balão da pergunta;
// embaixo, o do assistente respondendo (os três pontos). O brilho ao lado é o
// acento — é o assistente, e é com ele que a pessoa vai conversar.
export function AssistantIllustration(props: ComponentProps<"svg">) {
  return (
    <Illustration groundY={102} groundRx={44} {...props}>
      <path
        d="M74 16h44a10 10 0 0 1 10 10v24l-9-6H74a10 10 0 0 1-10-10v-8a10 10 0 0 1 10-10z"
        className="fill-current opacity-[0.06]"
      />
      <path
        d="M74 16h44a10 10 0 0 1 10 10v24l-9-6H74a10 10 0 0 1-10-10v-8a10 10 0 0 1 10-10z"
        className="stroke-current opacity-25"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <rect x="76" y="23" width="40" height="5" rx="2.5" className="fill-current opacity-20" />
      <rect x="76" y="32" width="26" height="5" rx="2.5" className="fill-current opacity-[0.15]" />
      <path
        d="M46 56h54a10 10 0 0 1 10 10v10a10 10 0 0 1-10 10H45l-9 6V66a10 10 0 0 1 10-10z"
        className="fill-current opacity-[0.06]"
      />
      <path
        d="M46 56h54a10 10 0 0 1 10 10v10a10 10 0 0 1-10 10H45l-9 6V66a10 10 0 0 1 10-10z"
        className="stroke-current opacity-25"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <circle cx="61" cy="71" r="3.5" className="fill-current opacity-30" />
      <circle cx="73" cy="71" r="3.5" className="fill-current opacity-25" />
      <circle cx="85" cy="71" r="3.5" className="fill-current opacity-20" />
      <path
        d="M24 36q2 8 10 10q-8 2-10 10q-2-8-10-10q8-2 10-10z"
        className="stroke-current text-primary"
        strokeWidth="2"
        strokeLinejoin="round"
      />
    </Illustration>
  );
}

// Tabulações: os desfechos que o vendedor escolhe ao encerrar. São etiquetas
// — e a de cima, marcada, é a que vira relatório.
export function DispositionIllustration(props: ComponentProps<"svg">) {
  return (
    <Illustration groundY={104} groundRx={38} {...props}>
      <rect x="28" y="20" width="104" height="74" rx="10" className="fill-current opacity-[0.06]" />
      <rect
        x="28"
        y="20"
        width="104"
        height="74"
        rx="10"
        className="stroke-current opacity-25"
        strokeWidth="2"
      />
      <rect
        x="42"
        y="32"
        width="62"
        height="14"
        rx="7"
        className="stroke-current text-primary"
        strokeWidth="2"
      />
      <path
        d="m84 39 4 4 8-8"
        className="stroke-current text-primary"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <rect
        x="54"
        y="37"
        width="22"
        height="4"
        rx="2"
        className="fill-current text-primary opacity-60"
      />
      <rect x="42" y="54" width="44" height="14" rx="7" className="fill-current opacity-[0.15]" />
      <rect x="42" y="76" width="60" height="14" rx="7" className="fill-current opacity-[0.15]" />
    </Illustration>
  );
}

// Ritmo de hoje: o gráfico de linha de ligações por hora, com as duas curvas
// ainda fantasmas — a de ontem tracejada, porque é só a referência, e a de
// hoje contínua. O ponto aceso no começo da curva de hoje é o acento: é o
// "agora", de onde a linha do dia sai assim que a primeira ligação acontecer.
export function PaceIllustration(props: ComponentProps<"svg">) {
  return (
    <Illustration groundY={106} groundRx={48} {...props}>
      <rect x="20" y="14" width="120" height="84" rx="10" className="fill-current opacity-[0.06]" />
      <rect
        x="20"
        y="14"
        width="120"
        height="84"
        rx="10"
        className="stroke-current opacity-25"
        strokeWidth="2"
      />
      {/* Linhas de grade: a escala existe, só não tem número ainda. */}
      {[36, 54, 72].map((y) => (
        <path key={y} d={`M34 ${y}h92`} className="stroke-current opacity-10" strokeWidth="1.5" />
      ))}
      <path
        d="M34 86h92"
        className="stroke-current opacity-25"
        strokeWidth="2"
        strokeLinecap="round"
      />
      {/* Ontem até esta hora: tracejada, é a régua de comparação. */}
      <path
        d="M36 80C50 78 56 60 72 58S100 42 124 30"
        className="stroke-current opacity-25"
        strokeWidth="2"
        strokeDasharray="4 4"
        strokeLinecap="round"
      />
      {/* Hoje: a curva que ainda vai ser desenhada. */}
      <path
        d="M40 78C54 76 62 70 78 68S106 54 124 48"
        className="stroke-current opacity-20"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
      <circle
        cx="40"
        cy="78"
        r="5"
        className="fill-background stroke-current text-primary"
        strokeWidth="2"
      />
    </Illustration>
  );
}

// Campanhas: o disparo para uma lista inteira. O megafone com as ondas em
// destaque é o alcance — o que a campanha faz e a lista sozinha não faz.
export function CampaignIllustration(props: ComponentProps<"svg">) {
  return (
    <Illustration groundY={100} groundRx={42} {...props}>
      <rect x="26" y="46" width="14" height="20" rx="5" className="fill-current opacity-25" />
      <path d="M40 44h8l40-18v56L48 64h-8z" className="fill-current opacity-[0.06]" />
      <path
        d="M40 44h8l40-18v56L48 64h-8z"
        className="stroke-current opacity-25"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path
        d="M100 42a18 18 0 0 1 0 26"
        className="stroke-current text-primary"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M110 32a30 30 0 0 1 0 46"
        className="stroke-current text-primary"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </Illustration>
  );
}

// Distribuição por status: a rosca das chamadas do período dividida por
// status, com a legenda ao lado — fantasma, porque sem chamada não há o que
// repartir. As fatias são desenhadas com `pathLength` 100, então cada traço é
// direto a fatia em pontos percentuais. A fatia maior é o acento: é a leitura
// que o card entrega, qual status pesa mais no período.
export function StatusBreakdownIllustration(props: ComponentProps<"svg">) {
  // [início, tamanho] de cada fatia, em % da volta; o vão de 2 entre elas
  // separa as fatias sem precisar de cor.
  const fatias: Array<[number, number, string]> = [
    [0, 36, "text-primary"],
    [38, 26, "opacity-30"],
    [66, 20, "opacity-20"],
    [88, 10, "opacity-25"],
  ];
  return (
    <Illustration groundY={106} groundRx={52} {...props}>
      <rect x="22" y="18" width="116" height="80" rx="10" className="fill-current opacity-[0.06]" />
      <rect
        x="22"
        y="18"
        width="116"
        height="80"
        rx="10"
        className="stroke-current opacity-25"
        strokeWidth="2"
      />
      {/* O trilho da rosca: a volta inteira, quase apagada, embaixo das fatias. */}
      <circle cx="60" cy="58" r="22" className="stroke-current opacity-[0.08]" strokeWidth="10" />
      {fatias.map(([inicio, tamanho, cls]) => (
        <circle
          key={inicio}
          cx="60"
          cy="58"
          r="22"
          pathLength={100}
          transform="rotate(-90 60 58)"
          strokeDasharray={`${tamanho} ${100 - tamanho}`}
          strokeDashoffset={-inicio}
          className={cn("stroke-current", cls)}
          strokeWidth="10"
        />
      ))}
      {/* A legenda: um marcador e uma linha por status, na ordem das fatias. */}
      {[42, 54, 66, 78].map((y, i) => (
        <g key={y}>
          <rect x="94" y={y - 3} width="6" height="6" rx="2" className="fill-current opacity-30" />
          <rect
            x="104"
            y={y - 2.5}
            width={[24, 18, 20, 14][i]}
            height="5"
            rx="2.5"
            className="fill-current opacity-20"
          />
        </g>
      ))}
    </Illustration>
  );
}

export function ConversationIllustration(props: ComponentProps<"svg">) {
  return (
    <Illustration groundY={108} groundRx={50} {...props}>
      <path
        d="M24 18h76a10 10 0 0 1 10 10v34a10 10 0 0 1-10 10H54L34 86V72H24a10 10 0 0 1-10-10V28a10 10 0 0 1 10-10Z"
        className="fill-current opacity-[0.06]"
      />
      <path
        d="M24 18h76a10 10 0 0 1 10 10v34a10 10 0 0 1-10 10H54L34 86V72H24a10 10 0 0 1-10-10V28a10 10 0 0 1 10-10Z"
        className="stroke-current opacity-25"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path
        d="M32 36h58M32 48h38"
        className="stroke-current opacity-25"
        strokeWidth="4"
        strokeLinecap="round"
      />
      <path
        d="M84 58h50a10 10 0 0 1 10 10v16a10 10 0 0 1-10 10h-4v10l-16-10H84a10 10 0 0 1-10-10V68a10 10 0 0 1 10-10Z"
        className="fill-background"
      />
      <path
        d="M84 58h50a10 10 0 0 1 10 10v16a10 10 0 0 1-10 10h-4v10l-16-10H84a10 10 0 0 1-10-10V68a10 10 0 0 1 10-10Z"
        className="stroke-current text-primary"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      {[94, 109, 124].map((cx) => (
        <circle key={cx} cx={cx} cy="76" r="3" className="fill-current text-primary opacity-60" />
      ))}
    </Illustration>
  );
}

// Objeções: o que o cliente diz e trava a conversa. O balão é a objeção; a
// pílula em destaque é a resposta pronta que a tela ainda não tem.
export function ObjectionIllustration(props: ComponentProps<"svg">) {
  return (
    <Illustration groundY={108} groundRx={38} {...props}>
      <path
        d="M32 24a8 8 0 0 1 8-8h56a8 8 0 0 1 8 8v26a8 8 0 0 1-8 8H58L44 70V58h-4a8 8 0 0 1-8-8z"
        className="fill-current opacity-[0.06]"
      />
      <path
        d="M32 24a8 8 0 0 1 8-8h56a8 8 0 0 1 8 8v26a8 8 0 0 1-8 8H58L44 70V58h-4a8 8 0 0 1-8-8z"
        className="stroke-current opacity-25"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <rect x="66" y="26" width="5" height="16" rx="2.5" className="fill-current opacity-30" />
      <circle cx="68.5" cy="48" r="3" className="fill-current opacity-30" />
      <rect
        x="56"
        y="80"
        width="66"
        height="18"
        rx="9"
        className="stroke-current text-primary"
        strokeWidth="2"
      />
      <rect
        x="68"
        y="87"
        width="42"
        height="4"
        rx="2"
        className="fill-current text-primary opacity-60"
      />
    </Illustration>
  );
}

// Funil da cadência: cada faixa é uma tentativa, e cada uma é mais estreita
// que a anterior porque menos leads chegam nela. As faixas são fantasmas —
// o recorte ainda não tem tentativa para medir. O acento é o que sai pelo
// bico do funil: o contato, que é a razão de insistir mais uma vez.
export function CadenceFunnelIllustration(props: ComponentProps<"svg">) {
  // Cada faixa: topo (y), altura e meia-largura no topo e na base.
  const faixas = [
    { y: 10, top: 56, bottom: 48 },
    { y: 30, top: 45, bottom: 37 },
    { y: 50, top: 34, bottom: 26 },
    { y: 70, top: 23, bottom: 15 },
  ];
  const altura = 16;
  // O conteúdo esmaece faixa a faixa, como os leads que vão ficando pelo caminho.
  const conteudo = ["opacity-30", "opacity-25", "opacity-20", "opacity-[0.15]"];
  return (
    <Illustration groundY={114} groundRx={30} {...props}>
      {faixas.map(({ y, top, bottom }, i) => {
        const d = `M${80 - top} ${y}h${top * 2}l${bottom - top} ${altura}h${-bottom * 2}z`;
        return (
          <g key={y}>
            <path d={d} className="fill-current opacity-[0.06]" />
            <path
              d={d}
              className="stroke-current opacity-25"
              strokeWidth="2"
              strokeLinejoin="round"
            />
            <rect
              x={80 - (bottom - 4) / 2}
              y={y + 6}
              width={bottom - 4}
              height="4"
              rx="2"
              className={cn("fill-current", conteudo[i])}
            />
          </g>
        );
      })}
      {/* O bico: o fim da escada de tentativas. */}
      <path
        d="M80 88v3"
        className="stroke-current opacity-25"
        strokeWidth="2"
        strokeLinecap="round"
        strokeDasharray="2 3"
      />
      {/* O contato que sai do funil. */}
      <circle
        cx="80"
        cy="102"
        r="10"
        className="fill-background stroke-current text-primary"
        strokeWidth="2"
      />
      <circle cx="80" cy="99" r="3" className="fill-current text-primary" />
      <path
        d="M74.5 108c1-2.8 3-4.2 5.5-4.2s4.5 1.4 5.5 4.2"
        className="stroke-current text-primary"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </Illustration>
  );
}

// Script de vendas: a conversa em etapas, na ordem em que o vendedor fala. O
// primeiro passo é o acento — é por ele que se começa a escrever.
export function ScriptIllustration(props: ComponentProps<"svg">) {
  return (
    <Illustration groundY={114} groundRx={34} {...props}>
      <rect x="38" y="8" width="84" height="98" rx="8" className="fill-current opacity-[0.06]" />
      <rect
        x="38"
        y="8"
        width="84"
        height="98"
        rx="8"
        className="stroke-current opacity-25"
        strokeWidth="2"
      />
      <path
        d="M58 32v48"
        className="stroke-current opacity-[0.15]"
        strokeWidth="2"
        strokeLinecap="round"
        strokeDasharray="4 6"
      />
      <circle cx="58" cy="30" r="7" className="stroke-current text-primary" strokeWidth="2" />
      <circle cx="58" cy="30" r="3" className="fill-current text-primary" />
      <rect x="72" y="27" width="34" height="6" rx="3" className="fill-current opacity-25" />
      <circle cx="58" cy="56" r="7" className="stroke-current opacity-25" strokeWidth="2" />
      <rect x="72" y="53" width="28" height="6" rx="3" className="fill-current opacity-20" />
      <circle cx="58" cy="82" r="7" className="stroke-current opacity-25" strokeWidth="2" />
      <rect x="72" y="79" width="22" height="6" rx="3" className="fill-current opacity-[0.15]" />
    </Illustration>
  );
}

// Prévia da ordem de discagem: os leads da marca, ainda fantasmas, entrando no
// funil deitado e saindo como a fila numerada do outro lado — vazia, porque não
// há lead para simular. O funil é o acento: é a regra de status (etapa do CRM
// e prioridade) que decide quem entra e em que posição.
export function StatusPreviewIllustration(props: ComponentProps<"svg">) {
  return (
    <Illustration groundY={102} groundRx={60} {...props}>
      {[30, 53, 76].map((y, i) => (
        <g key={y}>
          <rect
            x="10"
            y={y}
            width="46"
            height="14"
            rx="4"
            className="fill-current opacity-[0.06]"
          />
          <rect
            x="10"
            y={y}
            width="46"
            height="14"
            rx="4"
            className="stroke-current opacity-25"
            strokeWidth="2"
          />
          <circle cx="18" cy={y + 7} r="3" className="fill-current opacity-30" />
          <rect
            x="25"
            y={y + 5}
            width={i === 0 ? 24 : i === 1 ? 18 : 21}
            height="4"
            rx="2"
            className="fill-current opacity-20"
          />
        </g>
      ))}
      <path
        d="M64 28 86 52h8v16h-8L64 92z"
        className="stroke-current text-primary"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path
        d="M72 40v40M80 49v22"
        className="stroke-current text-primary opacity-60"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M97 60h6"
        className="stroke-current opacity-25"
        strokeWidth="2"
        strokeLinecap="round"
        strokeDasharray="2 3"
      />
      <rect x="106" y="26" width="46" height="68" rx="8" className="fill-current opacity-[0.06]" />
      <rect
        x="106"
        y="26"
        width="46"
        height="68"
        rx="8"
        className="stroke-current opacity-25"
        strokeWidth="2"
        strokeDasharray="4 4"
      />
      {[42, 60, 78].map((y, i) => (
        <g key={y}>
          <circle
            cx="117"
            cy={y}
            r="5"
            className={i === 0 ? "fill-current opacity-25" : "stroke-current opacity-25"}
            strokeWidth="1.5"
          />
          <rect
            x="126"
            y={y - 2}
            width={i === 0 ? 18 : i === 1 ? 14 : 10}
            height="4"
            rx="2"
            className="fill-current opacity-15"
          />
        </g>
      ))}
    </Illustration>
  );
}

// Entregas de automação: o disparo que sai daqui e chega do outro lado. O
// alvo com o check é o acento — a entrega só conta quando é aceita.
export function DeliveryIllustration(props: ComponentProps<"svg">) {
  return (
    <Illustration groundY={94} groundRx={44} {...props}>
      <rect x="18" y="34" width="56" height="44" rx="8" className="fill-current opacity-[0.06]" />
      <rect
        x="18"
        y="34"
        width="56"
        height="44"
        rx="8"
        className="stroke-current opacity-25"
        strokeWidth="2"
      />
      <rect x="30" y="46" width="32" height="6" rx="3" className="fill-current opacity-25" />
      <rect x="30" y="58" width="20" height="6" rx="3" className="fill-current opacity-[0.15]" />
      <path
        d="M82 56h20m-6-5 6 5-6 5"
        className="stroke-current opacity-25"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="124" cy="56" r="16" className="stroke-current text-primary" strokeWidth="2" />
      <path
        d="m116 56 6 6 10-12"
        className="stroke-current text-primary"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Illustration>
  );
}

// Etapas de conversão: as etapas do funil do CRM lado a lado, cada uma uma
// coluna com cartões fantasmas, encolhendo da esquerda para a direita como o
// funil encolhe. A última coluna é a etapa marcada — contorno e selo em
// text-primary, o acento —, porque a tela serve para isso: dizer quais etapas
// contam como conversão e quais como perda. O selo acompanha a lista: check
// na de conversão, X na de perda.
export function ConversionStagesIllustration({
  result = "CONVERSION",
  ...props
}: ComponentProps<"svg"> & { result?: "CONVERSION" | "LOSS" }) {
  const etapas = [
    { x: 19, y: 34, cartoes: 3 },
    { x: 51, y: 46, cartoes: 2 },
    { x: 83, y: 58, cartoes: 1 },
  ];
  return (
    <Illustration groundY={104} groundRx={56} {...props}>
      {etapas.map((etapa) => (
        <g key={etapa.x}>
          <rect
            x={etapa.x}
            y={etapa.y}
            width="26"
            height={96 - etapa.y}
            rx="6"
            className="fill-current opacity-[0.06]"
          />
          <rect
            x={etapa.x}
            y={etapa.y}
            width="26"
            height={96 - etapa.y}
            rx="6"
            className="stroke-current opacity-25"
            strokeWidth="2"
          />
          <rect
            x={etapa.x + 5}
            y={etapa.y + 6}
            width="12"
            height="3"
            rx="1.5"
            className="fill-current opacity-30"
          />
          {Array.from({ length: etapa.cartoes }, (_, i) => (
            <rect
              key={i}
              x={etapa.x + 5}
              y={etapa.y + 15 + i * 13}
              width="16"
              height="8"
              rx="2"
              className="fill-current opacity-20"
            />
          ))}
        </g>
      ))}
      <rect
        x="115"
        y="70"
        width="26"
        height="26"
        rx="6"
        className="fill-current text-primary opacity-10"
      />
      <rect
        x="115"
        y="70"
        width="26"
        height="26"
        rx="6"
        className="stroke-current text-primary"
        strokeWidth="2"
      />
      <rect
        x="120"
        y="76"
        width="12"
        height="3"
        rx="1.5"
        className="fill-current text-primary opacity-60"
      />
      <circle
        cx="128"
        cy="50"
        r="12"
        className="fill-background stroke-current text-primary"
        strokeWidth="2"
      />
      <path
        d={result === "LOSS" ? "m123.5 45.5 9 9m0-9-9 9" : "m122.5 50.5 4 4 7-8"}
        className="stroke-current text-primary"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Illustration>
  );
}

// Instalar o app: a janela do computador atrás e o celular na frente, cada um
// com a grade de ícones da área de trabalho. O acento é o ícone do Disk já
// instalado no celular — o resultado da ação, não o botão que a dispara.
export function InstallAppIllustration(props: ComponentProps<"svg">) {
  return (
    <Illustration groundY={110} groundRx={46} {...props}>
      {/* Janela do computador. */}
      <rect x="16" y="16" width="106" height="76" rx="9" className="fill-current opacity-[0.06]" />
      <rect
        x="16"
        y="16"
        width="106"
        height="76"
        rx="9"
        className="stroke-current opacity-25"
        strokeWidth="2"
      />
      {/* Barra de título com os três controles da janela. */}
      <path d="M16 32h106" className="stroke-current opacity-25" strokeWidth="2" />
      {[26, 34, 42].map((cx) => (
        <circle key={cx} cx={cx} cy="24" r="2.5" className="fill-current opacity-25" />
      ))}
      {/* Grade de ícones da área de trabalho. */}
      {[44, 66].map((y) =>
        [28, 50, 72].map((x) => (
          <rect
            key={`${x}-${y}`}
            x={x}
            y={y}
            width="14"
            height="14"
            rx="4"
            className="fill-current opacity-[0.16]"
          />
        )),
      )}

      {/* Celular na frente, recortando a janela: a mesma instalação nos dois. */}
      {/* Dois retângulos, não um: `opacity` no elemento inteiro deixaria o
          preenchimento translúcido e a grade da janela apareceria através do
          celular. O fundo fica sólido; só o contorno é atenuado. */}
      <rect x="98" y="38" width="46" height="66" rx="10" className="fill-background" />
      <rect
        x="98"
        y="38"
        width="46"
        height="66"
        rx="10"
        className="stroke-current opacity-25"
        strokeWidth="2"
      />
      <rect x="114" y="44" width="14" height="3" rx="1.5" className="fill-current opacity-25" />
      {/* O ícone do Disk instalado — o único acento. */}
      <rect x="106" y="54" width="14" height="14" rx="4" className="fill-current text-primary" />
      <rect x="124" y="54" width="14" height="14" rx="4" className="fill-current opacity-[0.16]" />
      <rect x="106" y="74" width="14" height="14" rx="4" className="fill-current opacity-[0.16]" />
      <rect x="124" y="74" width="14" height="14" rx="4" className="fill-current opacity-[0.16]" />
      <rect x="112" y="94" width="20" height="3" rx="1.5" className="fill-current opacity-25" />
    </Illustration>
  );
}

// Instalado, sem o peso do navegador: a barra de abas e a barra de endereço
// saem de cima da janela (o bloco solto lá em cima, atenuado) e o conteúdo
// ocupa o espaço que era delas — é o conteúdo que fica em primary.
export function NoBrowserChromeIllustration(props: ComponentProps<"svg">) {
  return (
    <Illustration groundY={108} groundRx={44} {...props}>
      {/* O que sai: barra de abas e barra de endereço, soltas e desalinhadas. */}
      <g transform="rotate(-4 80 22)">
        <rect x="26" y="8" width="32" height="9" rx="3" className="fill-current opacity-30" />
        <rect x="62" y="8" width="32" height="9" rx="3" className="fill-current opacity-20" />
        <rect
          x="26"
          y="22"
          width="98"
          height="9"
          rx="4.5"
          className="fill-current opacity-[0.18]"
        />
      </g>

      {/* A janela do app, já sem elas. */}
      <rect x="22" y="40" width="116" height="58" rx="10" className="fill-current opacity-[0.06]" />
      <rect
        x="22"
        y="40"
        width="116"
        height="58"
        rx="10"
        className="stroke-current opacity-25"
        strokeWidth="2"
      />
      {/* Conteúdo colado no topo: o espaço das barras virou tela útil. */}
      <rect x="34" y="52" width="52" height="7" rx="3.5" className="fill-current text-primary" />
      <rect x="34" y="66" width="92" height="6" rx="3" className="fill-current opacity-[0.18]" />
      <rect x="34" y="78" width="72" height="6" rx="3" className="fill-current opacity-[0.18]" />
    </Illustration>
  );
}

// Uma janela só sua: o Disk deixa de ser mais uma aba e vira janela com lugar
// na barra de tarefas — o acento é o ícone dele lá embaixo, entre os outros.
export function OwnWindowIllustration(props: ComponentProps<"svg">) {
  return (
    <Illustration groundY={112} groundRx={44} {...props}>
      {/* Janelas atrás: as outras coisas abertas na máquina. */}
      <rect
        x="34"
        y="12"
        width="98"
        height="52"
        rx="9"
        className="fill-background stroke-current opacity-[0.18]"
        strokeWidth="2"
      />
      {/* A janela do Disk, na frente e inteira. */}
      <rect x="20" y="24" width="98" height="54" rx="9" className="fill-background" />
      <rect x="20" y="24" width="98" height="54" rx="9" className="fill-current opacity-[0.06]" />
      <rect
        x="20"
        y="24"
        width="98"
        height="54"
        rx="9"
        className="stroke-current opacity-25"
        strokeWidth="2"
      />
      <rect x="32" y="36" width="40" height="6" rx="3" className="fill-current opacity-30" />
      <rect x="32" y="50" width="66" height="5" rx="2.5" className="fill-current opacity-[0.18]" />
      <rect x="32" y="61" width="50" height="5" rx="2.5" className="fill-current opacity-[0.18]" />

      {/* Barra de tarefas: o lugar reservado que uma aba nunca teve. */}
      <rect x="24" y="86" width="112" height="20" rx="9" className="fill-current opacity-[0.09]" />
      <rect x="34" y="91" width="10" height="10" rx="3" className="fill-current text-primary" />
      <rect x="50" y="91" width="10" height="10" rx="3" className="fill-current opacity-20" />
      <rect x="66" y="91" width="10" height="10" rx="3" className="fill-current opacity-20" />
    </Illustration>
  );
}

// Abre mais rápido: os arquivos do app já estão no aparelho, então a barra de
// carregamento termina antes — o trecho preenchido é o acento.
export function FastStartIllustration(props: ComponentProps<"svg">) {
  return (
    <Illustration groundY={106} groundRx={40} {...props}>
      <rect x="30" y="16" width="100" height="70" rx="10" className="fill-current opacity-[0.06]" />
      <rect
        x="30"
        y="16"
        width="100"
        height="70"
        rx="10"
        className="stroke-current opacity-25"
        strokeWidth="2"
      />
      {/* O ícone do app já pronto na tela. */}
      <rect x="66" y="28" width="28" height="28" rx="9" className="fill-current text-primary" />
      <path d="M82 33l-7 10h6l-3 8 8-10h-6l2-8z" className="fill-background" />
      {/* Barra de carregamento quase no fim. */}
      <rect x="44" y="66" width="72" height="8" rx="4" className="fill-current opacity-[0.18]" />
      <rect x="44" y="66" width="56" height="8" rx="4" className="fill-current text-primary" />
    </Illustration>
  );
}

// Papéis do sistema: o mesmo organograma nas três, mudando só o recorte que o
// papel enxerga — é exatamente o que o badge de escopo diz ("Vê tudo", "Vê a
// equipe", "Vê só o próprio"). A pessoa do papel é o acento; o que ela vê fica
// nítido e o resto esmaece.
type RoleScope = "director" | "manager" | "seller";

const ORG_NODES = [
  { id: "top", x: 80, y: 30 },
  { id: "mid-a", x: 48, y: 62 },
  { id: "mid-b", x: 112, y: 62 },
  { id: "a1", x: 30, y: 94 },
  { id: "a2", x: 66, y: 94 },
  { id: "b1", x: 94, y: 94 },
  { id: "b2", x: 130, y: 94 },
] as const;

const ORG_LINKS = [
  ["top", "mid-a"],
  ["top", "mid-b"],
  ["mid-a", "a1"],
  ["mid-a", "a2"],
  ["mid-b", "b1"],
  ["mid-b", "b2"],
] as const;

type OrgNodeId = (typeof ORG_NODES)[number]["id"];

const ROLE_SCOPE: Record<RoleScope, { self: OrgNodeId; visible: readonly OrgNodeId[] }> = {
  director: { self: "top", visible: ORG_NODES.map((n) => n.id) },
  manager: { self: "mid-a", visible: ["mid-a", "a1", "a2"] },
  seller: { self: "a2", visible: ["a2"] },
};

function OrgPerson({ x, y, className }: { x: number; y: number; className: string }) {
  return (
    <g className={className}>
      <circle cx={x} cy={y - 5} r="5" className="fill-current" />
      <path d={`M${x - 9} ${y + 9}a9 8 0 0 1 18 0z`} className="fill-current" />
    </g>
  );
}

function RoleScopeIllustration({ scope, ...props }: ComponentProps<"svg"> & { scope: RoleScope }) {
  const { self, visible } = ROLE_SCOPE[scope];
  const node = (id: OrgNodeId) => ORG_NODES.find((n) => n.id === id)!;
  const seen = (id: OrgNodeId) => visible.includes(id);
  const inside = visible.map(node);
  // Área do que o papel enxerga: um retângulo em volta dos nós visíveis.
  const box = {
    x: Math.min(...inside.map((n) => n.x)) - 16,
    y: Math.min(...inside.map((n) => n.y)) - 16,
    x2: Math.max(...inside.map((n) => n.x)) + 16,
    y2: Math.max(...inside.map((n) => n.y)) + 14,
  };
  return (
    <Illustration groundY={112} groundRx={60} {...props}>
      <rect
        x={box.x}
        y={box.y}
        width={box.x2 - box.x}
        height={box.y2 - box.y}
        rx="10"
        className="fill-current text-primary opacity-[0.08]"
      />
      <rect
        x={box.x}
        y={box.y}
        width={box.x2 - box.x}
        height={box.y2 - box.y}
        rx="10"
        className="stroke-current text-primary opacity-40"
        strokeWidth="1.5"
        strokeDasharray="4 3"
      />
      {ORG_LINKS.map(([from, to]) => {
        const a = node(from);
        const b = node(to);
        return (
          <path
            key={`${from}-${to}`}
            d={`M${a.x} ${a.y + 10}V${(a.y + b.y) / 2 + 2}H${b.x}V${b.y - 12}`}
            className={cn("stroke-current", seen(from) && seen(to) ? "opacity-30" : "opacity-10")}
            strokeWidth="1.5"
            strokeLinejoin="round"
          />
        );
      })}
      {ORG_NODES.map((n) => (
        <OrgPerson
          key={n.id}
          x={n.x}
          y={n.y}
          className={n.id === self ? "text-primary" : seen(n.id) ? "opacity-35" : "opacity-[0.12]"}
        />
      ))}
    </Illustration>
  );
}

export function DirectorRoleIllustration(props: ComponentProps<"svg">) {
  return <RoleScopeIllustration scope="director" {...props} />;
}

export function ManagerRoleIllustration(props: ComponentProps<"svg">) {
  return <RoleScopeIllustration scope="manager" {...props} />;
}

export function OperatorRoleIllustration(props: ComponentProps<"svg">) {
  return <RoleScopeIllustration scope="seller" {...props} />;
}

// Meu desempenho, hoje sem nada ainda: o painel da pessoa com os números
// zerados — ela no topo, os três blocos fantasmas embaixo. O acento é o
// telefone saindo da moldura: a primeira ligação é o que acende o resto.
export function MyDayIllustration(props: ComponentProps<"svg">) {
  return (
    <Illustration groundY={106} groundRx={42} {...props}>
      <rect x="22" y="16" width="108" height="76" rx="10" className="fill-current opacity-[0.06]" />
      <rect
        x="22"
        y="16"
        width="108"
        height="76"
        rx="10"
        className="stroke-current opacity-25"
        strokeWidth="2"
      />
      <circle cx="40" cy="34" r="7" className="fill-current opacity-25" />
      <rect x="53" y="29" width="36" height="5" rx="2.5" className="fill-current opacity-25" />
      <rect x="53" y="37" width="22" height="4" rx="2" className="fill-current opacity-15" />
      {[32, 62, 92].map((x) => (
        <g key={x}>
          <rect
            x={x}
            y="52"
            width="26"
            height="28"
            rx="6"
            className="fill-current opacity-[0.08]"
          />
          <rect
            x={x + 5}
            y="58"
            width="10"
            height="5"
            rx="2.5"
            className="fill-current opacity-20"
          />
          <rect
            x={x + 5}
            y="68"
            width="16"
            height="4"
            rx="2"
            className="fill-current opacity-[0.12]"
          />
        </g>
      ))}
      <circle cx="128" cy="86" r="16" className="fill-current text-primary" />
      <path
        d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"
        className="stroke-background"
        transform="translate(121 79) scale(0.6)"
        strokeWidth="3.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Illustration>
  );
}

// Meu desempenho num período sem nada: o calendário com os dias fantasmas.
// O acento é a semana marcada — trocar o período é o único jeito de ver
// número aqui.
export function MyPeriodIllustration(props: ComponentProps<"svg">) {
  const cols = [42, 55, 68, 81, 94, 107, 120];
  const rows = [48, 60, 72, 84];
  return (
    <Illustration groundY={106} groundRx={42} {...props}>
      <rect x="30" y="18" width="102" height="78" rx="10" className="fill-current opacity-[0.06]" />
      <rect
        x="30"
        y="18"
        width="102"
        height="78"
        rx="10"
        className="stroke-current opacity-25"
        strokeWidth="2"
      />
      <path d="M31 34h100" className="stroke-current opacity-25" strokeWidth="2" />
      {[54, 108].map((x) => (
        <rect
          key={x}
          x={x - 2}
          y="12"
          width="4"
          height="12"
          rx="2"
          className="fill-current opacity-30"
        />
      ))}
      <rect x="40" y="24" width="30" height="5" rx="2.5" className="fill-current opacity-25" />
      {rows.map((y, r) =>
        cols.map((x, c) =>
          r === 1 && c >= 1 && c <= 5 ? null : (
            <circle key={`${x}-${y}`} cx={x} cy={y} r="2.5" className="fill-current opacity-20" />
          ),
        ),
      )}
      <rect
        x="48"
        y="53"
        width="66"
        height="14"
        rx="7"
        className="fill-current text-primary opacity-15"
      />
      <rect
        x="48"
        y="53"
        width="66"
        height="14"
        rx="7"
        className="stroke-current text-primary"
        strokeWidth="2"
      />
      {cols.slice(1, 6).map((x) => (
        <circle key={x} cx={x} cy="60" r="2.5" className="fill-current text-primary" />
      ))}
    </Illustration>
  );
}

// Consumo › plano: o cartão do plano contratado, com o nome e as linhas do que
// ele inclui. O selo de check no canto é o acento: é o que já está pago na
// mensalidade, sem franquia a consumir.
export function PlanIllustration(props: ComponentProps<"svg">) {
  return (
    <Illustration groundY={100} groundRx={40} {...props}>
      <rect x="36" y="18" width="84" height="72" rx="10" className="fill-current opacity-[0.06]" />
      <rect
        x="36"
        y="18"
        width="84"
        height="72"
        rx="10"
        className="stroke-current opacity-25"
        strokeWidth="2"
      />
      <rect x="48" y="30" width="36" height="7" rx="3.5" className="fill-current opacity-30" />
      {[48, 60, 72].map((y) => (
        <g key={y}>
          <circle cx="51" cy={y + 2.5} r="3" className="fill-current opacity-20" />
          <rect x="58" y={y} width="40" height="5" rx="2.5" className="fill-current opacity-20" />
        </g>
      ))}
      <circle
        cx="118"
        cy="24"
        r="13"
        className="fill-background stroke-current text-primary"
        strokeWidth="2"
      />
      <path
        d="m112 24 4 4 8-8"
        className="stroke-current text-primary"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Illustration>
  );
}

// Consumo › minutos: o fone da ligação com a onda da conversa saindo dele, e o
// relógio no canto como acento — a franquia é contada em minuto falado.
export function CallMinutesIllustration(props: ComponentProps<"svg">) {
  const onda = [8, 16, 24, 14, 20, 10];
  return (
    <Illustration groundY={100} groundRx={44} {...props}>
      <rect x="24" y="30" width="92" height="56" rx="12" className="fill-current opacity-[0.06]" />
      <rect
        x="24"
        y="30"
        width="92"
        height="56"
        rx="12"
        className="stroke-current opacity-25"
        strokeWidth="2"
      />
      {/* Fone: o mesmo contorno do ícone de telefone, em escala. */}
      <path
        d="M36 48c0 14 10 24 24 24l4-6-7-4-3 3c-4-2-7-5-9-9l3-3-4-7z"
        className="stroke-current opacity-30"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      {onda.map((h, i) => (
        <rect
          key={i}
          x={72 + i * 6}
          y={58 - h / 2}
          width="3"
          height={h}
          rx="1.5"
          className="fill-current opacity-25"
        />
      ))}
      <circle
        cx="118"
        cy="30"
        r="14"
        className="fill-background stroke-current text-primary"
        strokeWidth="2"
      />
      <path
        d="M118 22v8l5 4"
        className="stroke-current text-primary"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Illustration>
  );
}

// Consumo › armazenamento: as gravações guardadas, empilhadas como discos, com
// a faixa de uso acesa no de cima — o acento é o espaço que o mês ocupou.
export function StorageIllustration(props: ComponentProps<"svg">) {
  return (
    <Illustration groundY={100} groundRx={46} {...props}>
      {[62, 40, 18].map((y) => (
        <g key={y}>
          <rect
            x="34"
            y={y}
            width="92"
            height="22"
            rx="7"
            className="fill-current opacity-[0.06]"
          />
          <rect
            x="34"
            y={y}
            width="92"
            height="22"
            rx="7"
            className="stroke-current opacity-25"
            strokeWidth="2"
          />
          <circle cx="112" cy={y + 11} r="3" className="fill-current opacity-30" />
          <rect
            x="46"
            y={y + 8.5}
            width="26"
            height="5"
            rx="2.5"
            className="fill-current opacity-20"
          />
        </g>
      ))}
      <rect x="46" y="26.5" width="46" height="5" rx="2.5" className="fill-current text-primary" />
      <circle cx="112" cy="29" r="3" className="fill-current text-primary" />
    </Illustration>
  );
}

// Click to call: o cartão do negócio no CRM — nome, telefone e as linhas do
// registro — com o botão de ligar e o cursor sobre ele. O botão é o acento: é
// o clique que abre o console do Disk numa aba própria.
export function ClickToCallIllustration(props: ComponentProps<"svg">) {
  return (
    <Illustration groundY={104} groundRx={46} {...props}>
      <rect x="26" y="16" width="100" height="78" rx="10" className="fill-current opacity-[0.06]" />
      <rect
        x="26"
        y="16"
        width="100"
        height="78"
        rx="10"
        className="stroke-current opacity-25"
        strokeWidth="2"
      />
      <circle cx="44" cy="36" r="8" className="fill-current opacity-20" />
      <rect x="58" y="30" width="40" height="6" rx="3" className="fill-current opacity-30" />
      <rect x="58" y="40" width="26" height="4" rx="2" className="fill-current opacity-20" />
      {[56, 66].map((y) => (
        <rect
          key={y}
          x="38"
          y={y}
          width="56"
          height="4"
          rx="2"
          className="fill-current opacity-20"
        />
      ))}
      <rect x="38" y="76" width="44" height="12" rx="6" className="fill-current text-primary" />
      <path
        d="M52 82h16m-4-3 3 3-3 3"
        className="stroke-background"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* Cursor: a mão da pessoa, pousada sobre o botão. */}
      <path
        d="m78 84 0 16 4-4 3 7 3-1-3-7h6z"
        className="fill-background stroke-current opacity-60"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </Illustration>
  );
}
